'use client';

import React, { useState } from 'react';
import { Plus, Search, Eye, CheckCircle, XCircle, Clock, X, FileText, ShoppingCart, Filter, ChevronDown, User, DollarSign, Package, ArrowDownRight, AlertTriangle, Printer, Download } from 'lucide-react';
import { FARMS, MOCK_FLOCK_SUMMARIES } from '../../components/birdRecordsData';
import { useSaleRecords, useCreateSaleRecord, useUpdateSaleRecord } from '@/hooks/useSaleRecords';
import { type BirdSaleRecord } from '@/lib/birdRecordsApi';
import { toast } from 'sonner';

// ─── Types ────────────────────────────────────────────────────────────────────

type BirdSaleStatus = 'draft' | 'pending_approval' | 'confirmed' | 'posted' | 'rejected' | 'cancelled';
type BirdCategory = 'broiler' | 'layer' | 'breeder' | 'chick' | 'pullet' | 'cockerel' | 'other';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<BirdSaleStatus, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  draft: { label: 'Draft', color: '#64748b', bg: '#f1f5f9', icon: FileText },
  pending_approval: { label: 'Pending Approval', color: '#d97706', bg: '#fef3c7', icon: Clock },
  confirmed: { label: 'Confirmed', color: '#2563eb', bg: '#dbeafe', icon: CheckCircle },
  posted: { label: 'Posted', color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', icon: XCircle },
  cancelled: { label: 'Cancelled', color: '#6b7280', bg: '#f3f4f6', icon: X },
};

const PAYMENT_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  paid: { label: 'Paid', color: '#16a34a', bg: '#dcfce7' },
  partial: { label: 'Partial', color: '#d97706', bg: '#fef3c7' },
  unpaid: { label: 'Unpaid', color: '#64748b', bg: '#f1f5f9' },
};

const DELIVERY_LABELS: Record<string, string> = {
  farm_gate: 'Farm Gate',
  delivered: 'Delivered',
  collected: 'Collected by Buyer',
};

const PAYMENT_METHODS = ['Cash', 'Mobile Money', 'Bank Transfer', 'Cheque', 'Credit'];
const APPROVERS = ['Farm Manager', 'Operations Director', 'General Manager', 'Dr. Sarah Namukasa'];
const RECORDERS = ['James Okello', 'Moses Wafula', 'Sarah Namukasa', 'Peter Ssemakula', 'Grace Atim'];
const CUSTOMERS = [
  'Kampala Fresh Markets Ltd', 'Owino Supermarket', 'Mr. David Ssemakula',
  'Jinja Poultry Traders', 'Entebbe Hotel & Suites', 'Ms. Grace Nakato', 'Walk-in Customer',
];

// ─── KPI Summary ──────────────────────────────────────────────────────────────

function SalesSummaryCards({ records }: { records: BirdSaleRecord[] }) {
  const posted = records.filter(r => r.status === 'posted');
  const pending = records.filter(r => r.status === 'pending_approval');
  const confirmed = records.filter(r => r.status === 'confirmed');
  const totalBirdsSold = posted.reduce((s, r) => s + r.quantity, 0);
  const totalRevenue = posted.reduce((s, r) => s + r.totalRevenue, 0);
  const totalCollected = posted.reduce((s, r) => s + r.amountPaid, 0);
  const totalOutstanding = posted.reduce((s, r) => s + r.balance, 0);

  const cards = [
    { label: 'Birds Sold (Posted)', value: totalBirdsSold.toLocaleString(), icon: Package, color: '#9333ea', bg: '#f3e8ff' },
    { label: 'Total Revenue', value: `UGX ${(totalRevenue / 1000000).toFixed(1)}M`, icon: DollarSign, color: '#16a34a', bg: '#dcfce7' },
    { label: 'Amount Collected', value: `UGX ${(totalCollected / 1000000).toFixed(1)}M`, icon: CheckCircle, color: '#2563eb', bg: '#dbeafe' },
    { label: 'Outstanding Balance', value: `UGX ${(totalOutstanding / 1000000).toFixed(1)}M`, icon: AlertTriangle, color: '#d97706', bg: '#fef3c7' },
    { label: 'Pending Approval', value: pending.length.toString(), icon: Clock, color: '#ea580c', bg: '#ffedd5' },
    { label: 'Confirmed (Unposted)', value: confirmed.length.toString(), icon: ArrowDownRight, color: '#0891b2', bg: '#cffafe' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-border/60 shadow-sm p-4 flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg flex-shrink-0" style={{ backgroundColor: c.bg }}>
            <c.icon className="h-4 w-4" style={{ color: c.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground font-medium truncate">{c.label}</p>
            <p className="text-xl font-semibold tracking-tight text-foreground">{c.value}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Record Sale Form ─────────────────────────────────────────────────────────

interface RecordSaleFormProps {
  onClose: () => void;
  onSubmit: (data: Omit<BirdSaleRecord, 'id' | 'saleNumber' | 'createdAt'>) => void;
  isPending: boolean;
}

function RecordSaleForm({ onClose, onSubmit, isPending }: RecordSaleFormProps) {
  const [form, setForm] = useState({
    saleDate: new Date().toISOString().split('T')[0],
    farm: '', house: '', flock: '', flockNumber: '',
    birdType: '', breed: '', birdCategory: 'broiler' as BirdCategory,
    quantity: '', unitPrice: '',
    customerName: '', customerContact: '',
    paymentMethod: 'Cash',
    paymentStatus: 'unpaid\' as \'paid\' | \'partial\' | \'unpaid',
    amountPaid: '',
    deliveryMethod: 'farm_gate\' as \'farm_gate\' | \'delivered\' | \'collected',
    authorizedBy: '', recordedBy: '', notes: '',
    status: 'draft' as BirdSaleStatus,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const qty = parseInt(form.quantity) || 0;
  const unitPrice = parseFloat(form.unitPrice) || 0;
  const totalRevenue = qty * unitPrice;
  const amountPaid = parseFloat(form.amountPaid) || 0;
  const balance = totalRevenue - amountPaid;

  const selectedFlock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === form.flockNumber);
  const currentPop = selectedFlock?.currentBirds || 0;
  const projectedPop = currentPop - qty;
  const isNegativePop = projectedPop < 0;
  const isLargeQty = qty > 0 && currentPop > 0 && qty / currentPop > 0.5;

  function handleFlockSelect(flockNumber: string) {
    const flock = MOCK_FLOCK_SUMMARIES.find(f => f.flockNumber === flockNumber);
    if (flock) {
      setForm(prev => ({
        ...prev, flockNumber: flock.flockNumber, flock: flock.flockName,
        farm: flock.farm, house: flock.house, birdType: flock.birdType,
        breed: flock.breed, birdCategory: flock.birdType.toLowerCase() as BirdCategory,
      }));
    }
  }

  function validate() {
    const e: Record<string, string> = {};
    if (!form.saleDate) e.saleDate = 'Sale date is required';
    if (!form.flockNumber) e.flockNumber = 'Flock is required';
    if (!form.quantity || qty <= 0) e.quantity = 'Quantity must be greater than 0';
    if (isNegativePop) e.quantity = `Quantity exceeds available birds (${currentPop.toLocaleString()})`;
    if (!form.unitPrice || unitPrice <= 0) e.unitPrice = 'Unit price is required';
    if (!form.customerName) e.customerName = 'Customer name is required';
    if (!form.authorizedBy) e.authorizedBy = 'Authorization is required';
    if (!form.recordedBy) e.recordedBy = 'Recorded by is required';
    return e;
  }

  function handleSubmit(submitStatus: BirdSaleStatus) {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }
    onSubmit({
      saleDate: form.saleDate, farm: form.farm, house: form.house,
      flock: form.flock, flockNumber: form.flockNumber, birdType: form.birdType,
      breed: form.breed, birdCategory: form.birdCategory, quantity: qty,
      unitPrice, totalRevenue, currency: 'UGX',
      populationBefore: currentPop, populationAfter: projectedPop,
      customerName: form.customerName, customerContact: form.customerContact,
      paymentMethod: form.paymentMethod, paymentStatus: form.paymentStatus,
      amountPaid, balance, deliveryMethod: form.deliveryMethod,
      authorizedBy: form.authorizedBy, recordedBy: form.recordedBy,
      notes: form.notes, status: submitStatus,
      submittedAt: submitStatus !== 'draft' ? new Date().toISOString() : undefined,
    });
  }

  const inputCls = (field: string) =>
    `w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${errors[field] ? 'border-red-400 bg-red-50' : 'border-border bg-white'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30 backdrop-blur-sm">
      <div className="h-full w-full max-w-xl bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-white">
          <div>
            <h2 className="text-base font-semibold text-foreground">Record Bird Sale</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Population-OUT movement will be created on confirmation</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Section A */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <ShoppingCart className="h-3.5 w-3.5" /> A — Sale Details
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground mb-1">Sale Date <span className="text-red-500">*</span></label>
                <input type="date" className={inputCls('saleDate')} value={form.saleDate}
                  onChange={e => setForm(p => ({ ...p, saleDate: e.target.value }))} />
                {errors.saleDate && <p className="text-xs text-red-500 mt-1">{errors.saleDate}</p>}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground mb-1">Flock <span className="text-red-500">*</span></label>
                <select className={inputCls('flockNumber')} value={form.flockNumber}
                  onChange={e => handleFlockSelect(e.target.value)}>
                  <option value="">Select flock…</option>
                  {MOCK_FLOCK_SUMMARIES.map(f => (
                    <option key={f.flockId} value={f.flockNumber}>
                      {f.flockNumber} — {f.flockName} ({f.currentBirds.toLocaleString()} birds)
                    </option>
                  ))}
                </select>
                {errors.flockNumber && <p className="text-xs text-red-500 mt-1">{errors.flockNumber}</p>}
              </div>
              {form.farm && (
                <>
                  <div><label className="block text-xs font-medium text-foreground mb-1">Farm</label>
                    <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-gray-50 text-muted-foreground" value={form.farm} readOnly /></div>
                  <div><label className="block text-xs font-medium text-foreground mb-1">House</label>
                    <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-gray-50 text-muted-foreground" value={form.house} readOnly /></div>
                  <div><label className="block text-xs font-medium text-foreground mb-1">Bird Type</label>
                    <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-gray-50 text-muted-foreground" value={form.birdType} readOnly /></div>
                  <div><label className="block text-xs font-medium text-foreground mb-1">Breed</label>
                    <input className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-gray-50 text-muted-foreground" value={form.breed} readOnly /></div>
                </>
              )}
            </div>
          </div>

          {/* Section B */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Package className="h-3.5 w-3.5" /> B — Birds & Pricing
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Quantity <span className="text-red-500">*</span></label>
                <input type="number" min="1" className={inputCls('quantity')} placeholder="0"
                  value={form.quantity} onChange={e => setForm(p => ({ ...p, quantity: e.target.value }))} />
                {errors.quantity && <p className="text-xs text-red-500 mt-1">{errors.quantity}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Unit Price (UGX) <span className="text-red-500">*</span></label>
                <input type="number" min="0" className={inputCls('unitPrice')} placeholder="0"
                  value={form.unitPrice} onChange={e => setForm(p => ({ ...p, unitPrice: e.target.value }))} />
                {errors.unitPrice && <p className="text-xs text-red-500 mt-1">{errors.unitPrice}</p>}
              </div>
            </div>
            {form.flockNumber && qty > 0 && (
              <div className={`mt-3 rounded-lg border p-3 ${isNegativePop ? 'border-red-300 bg-red-50' : isLargeQty ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
                <p className="text-xs font-semibold mb-2" style={{ color: isNegativePop ? '#dc2626' : isLargeQty ? '#d97706' : '#16a34a' }}>
                  {isNegativePop ? '⛔ Insufficient Birds' : isLargeQty ? '⚠ Large Sale — Approval Required' : '✓ Population Impact'}
                </p>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="text-center"><p className="text-muted-foreground">Current</p><p className="font-semibold text-foreground">{currentPop.toLocaleString()}</p></div>
                  <div className="text-center"><p className="text-muted-foreground">Selling</p><p className="font-semibold text-red-600">−{qty.toLocaleString()}</p></div>
                  <div className="text-center"><p className="text-muted-foreground">Remaining</p><p className={`font-semibold ${isNegativePop ? 'text-red-600' : 'text-foreground'}`}>{projectedPop.toLocaleString()}</p></div>
                </div>
                {totalRevenue > 0 && (
                  <div className="mt-2 pt-2 border-t border-current/10 flex justify-between text-xs">
                    <span className="text-muted-foreground">Total Revenue</span>
                    <span className="font-semibold text-foreground">UGX {totalRevenue.toLocaleString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section C */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <DollarSign className="h-3.5 w-3.5" /> C — Customer & Payment
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground mb-1">Customer Name <span className="text-red-500">*</span></label>
                <input list="customer-list" className={inputCls('customerName')} placeholder="Customer name or company"
                  value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} />
                <datalist id="customer-list">{CUSTOMERS.map(c => <option key={c} value={c} />)}</datalist>
                {errors.customerName && <p className="text-xs text-red-500 mt-1">{errors.customerName}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Contact</label>
                <input type="text" className={inputCls('customerContact')} placeholder="+256 …"
                  value={form.customerContact} onChange={e => setForm(p => ({ ...p, customerContact: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Delivery Method</label>
                <select className={inputCls('deliveryMethod')} value={form.deliveryMethod}
                  onChange={e => setForm(p => ({ ...p, deliveryMethod: e.target.value as typeof form.deliveryMethod }))}>
                  <option value="farm_gate">Farm Gate</option>
                  <option value="delivered">Delivered</option>
                  <option value="collected">Collected by Buyer</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Payment Method</label>
                <select className={inputCls('paymentMethod')} value={form.paymentMethod}
                  onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                  {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Payment Status</label>
                <select className={inputCls('paymentStatus')} value={form.paymentStatus}
                  onChange={e => setForm(p => ({ ...p, paymentStatus: e.target.value as typeof form.paymentStatus }))}>
                  <option value="unpaid">Unpaid</option>
                  <option value="partial">Partial</option>
                  <option value="paid">Paid in Full</option>
                </select>
              </div>
              {(form.paymentStatus === 'partial' || form.paymentStatus === 'paid') && (
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-foreground mb-1">Amount Paid (UGX)</label>
                  <input type="number" min="0" className={inputCls('amountPaid')} placeholder="0"
                    value={form.amountPaid} onChange={e => setForm(p => ({ ...p, amountPaid: e.target.value }))} />
                  {totalRevenue > 0 && amountPaid > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Balance: UGX {Math.max(0, balance).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Section D */}
          <div>
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <User className="h-3.5 w-3.5" /> D — Authorization
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Authorized By <span className="text-red-500">*</span></label>
                <select className={inputCls('authorizedBy')} value={form.authorizedBy}
                  onChange={e => setForm(p => ({ ...p, authorizedBy: e.target.value }))}>
                  <option value="">Select…</option>
                  {APPROVERS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
                {errors.authorizedBy && <p className="text-xs text-red-500 mt-1">{errors.authorizedBy}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground mb-1">Recorded By <span className="text-red-500">*</span></label>
                <select className={inputCls('recordedBy')} value={form.recordedBy}
                  onChange={e => setForm(p => ({ ...p, recordedBy: e.target.value }))}>
                  <option value="">Select…</option>
                  {RECORDERS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                {errors.recordedBy && <p className="text-xs text-red-500 mt-1">{errors.recordedBy}</p>}
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium text-foreground mb-1">Notes</label>
                <textarea rows={2} className={`${inputCls('notes')} resize-none`} placeholder="Optional notes…"
                  value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t bg-gray-50 flex items-center gap-2 flex-wrap">
          <button onClick={() => handleSubmit('draft')} disabled={isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">
            Save Draft
          </button>
          <button onClick={() => handleSubmit('pending_approval')} disabled={isNegativePop || isPending}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {isPending ? 'Saving…' : 'Submit for Approval'}
          </button>
          <button onClick={onClose} className="ml-auto px-4 py-2 text-sm font-medium rounded-lg text-muted-foreground hover:bg-gray-100 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Drawer ────────────────────────────────────────────────────────────

interface DetailDrawerProps {
  record: BirdSaleRecord;
  onClose: () => void;
  onApprove: (id: string) => void;
  onReject: (id: string, reason: string) => void;
  onPost: (id: string) => void;
  onCancel: (id: string) => void;
  isMutating: boolean;
}

function DetailDrawer({ record, onClose, onApprove, onReject, onPost, onCancel, isMutating }: DetailDrawerProps) {
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectInput, setShowRejectInput] = useState(false);
  const sc = STATUS_CONFIG[record.status];
  const pc = PAYMENT_STATUS_CONFIG[record.paymentStatus];

  const timeline = [
    { label: 'Created', time: record.createdAt, done: true },
    { label: 'Submitted', time: record.submittedAt, done: !!record.submittedAt },
    { label: 'Confirmed', time: record.confirmedAt, done: !!record.confirmedAt },
    { label: 'Posted — Population Updated', time: record.postedAt, done: !!record.postedAt },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end bg-black/30 backdrop-blur-sm">
      <div className="h-full w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-foreground">{record.saleNumber}</h2>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ backgroundColor: sc.bg, color: sc.color }}>
                <sc.icon className="h-3 w-3" />{sc.label}
              </span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">{record.saleDate} · {record.farm}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Population Impact */}
          <div className="rounded-xl border border-border/60 bg-gray-50 p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Population-OUT Movement</p>
            <div className="grid grid-cols-3 gap-3 text-center">
              <div><p className="text-xs text-muted-foreground">Before Sale</p><p className="text-lg font-semibold text-foreground">{record.populationBefore?.toLocaleString() ?? '—'}</p></div>
              <div><p className="text-xs text-muted-foreground">Birds Sold</p><p className="text-lg font-semibold text-red-600">−{record.quantity.toLocaleString()}</p></div>
              <div><p className="text-xs text-muted-foreground">After Sale</p><p className="text-lg font-semibold text-foreground">{record.populationAfter?.toLocaleString() ?? '—'}</p></div>
            </div>
            {record.movementId && (
              <div className="mt-3 pt-3 border-t border-border/60 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Movement Reference</span>
                <span className="font-medium text-primary">{record.movementId}</span>
              </div>
            )}
          </div>

          {/* Revenue Traceability */}
          <div className="rounded-xl border border-border/60 p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Revenue Traceability</p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Quantity</span><span className="font-medium">{record.quantity.toLocaleString()} birds</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Unit Price</span><span className="font-medium">UGX {record.unitPrice.toLocaleString()}</span></div>
              <div className="flex justify-between border-t pt-2"><span className="font-semibold">Total Revenue</span><span className="font-semibold text-green-700">UGX {record.totalRevenue.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Amount Collected</span><span className="font-medium text-blue-700">UGX {record.amountPaid.toLocaleString()}</span></div>
              {record.balance > 0 && (
                <div className="flex justify-between"><span className="text-muted-foreground">Outstanding Balance</span><span className="font-medium text-amber-700">UGX {record.balance.toLocaleString()}</span></div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Payment Status</span>
                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: pc.bg, color: pc.color }}>{pc.label}</span>
              </div>
              {record.referenceInvoice && (
                <div className="flex justify-between"><span className="text-muted-foreground">Invoice Reference</span><span className="font-medium text-primary">{record.referenceInvoice}</span></div>
              )}
            </div>
          </div>

          {/* Sale Details */}
          <div className="rounded-xl border border-border/60 p-4 space-y-2 text-sm">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Sale Details</p>
            {([
              ['Flock', `${record.flockNumber} — ${record.flock}`],
              ['Bird Type', record.birdType], ['Breed', record.breed],
              ['Farm', record.farm], ['House', record.house],
              ['Customer', record.customerName], ['Contact', record.customerContact || '—'],
              ['Delivery', DELIVERY_LABELS[record.deliveryMethod]],
              ['Payment Method', record.paymentMethod],
              ['Authorized By', record.authorizedBy],
              ['Recorded By', record.recordedBy],
              ['Approved By', record.approvedBy || '—'],
            ] as [string, string][]).map(([k, v]) => (
              <div key={k} className="flex justify-between">
                <span className="text-muted-foreground">{k}</span>
                <span className="font-medium text-right max-w-[55%]">{v}</span>
              </div>
            ))}
            {record.notes && (
              <div className="pt-2 border-t"><p className="text-xs text-muted-foreground mb-1">Notes</p><p className="text-sm">{record.notes}</p></div>
            )}
          </div>

          {record.status === 'rejected' && record.rejectionReason && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-xs font-semibold text-red-700 mb-1">Rejection Reason</p>
              <p className="text-sm text-red-800">{record.rejectionReason}</p>
              {record.rejectedBy && <p className="text-xs text-red-600 mt-1">By: {record.rejectedBy}</p>}
            </div>
          )}

          {/* Timeline */}
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Workflow Timeline</p>
            <div className="space-y-2">
              {timeline.map((step, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className={`h-6 w-6 rounded-full flex items-center justify-center flex-shrink-0 ${step.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                    {step.done ? <CheckCircle className="h-3.5 w-3.5 text-green-600" /> : <div className="h-2 w-2 rounded-full bg-gray-300" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs font-medium ${step.done ? 'text-foreground' : 'text-muted-foreground'}`}>{step.label}</p>
                    {step.time && <p className="text-xs text-muted-foreground">{new Date(step.time).toLocaleString()}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showRejectInput && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <label className="block text-xs font-medium text-red-700 mb-2">Rejection Reason <span className="text-red-500">*</span></label>
              <textarea rows={2} className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="Enter reason for rejection…" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
              <div className="flex gap-2 mt-2">
                <button onClick={() => { if (rejectReason.trim()) { onReject(record.id, rejectReason); setShowRejectInput(false); } }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
                  Confirm Rejection
                </button>
                <button onClick={() => setShowRejectInput(false)}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t bg-gray-50 flex flex-wrap gap-2">
          {record.status === 'pending_approval' && (
            <>
              <button onClick={() => onApprove(record.id)} disabled={isMutating}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                <CheckCircle className="h-3.5 w-3.5" /> Confirm Sale
              </button>
              <button onClick={() => setShowRejectInput(true)} disabled={isMutating}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center gap-1.5 disabled:opacity-50">
                <XCircle className="h-3.5 w-3.5" /> Reject
              </button>
            </>
          )}
          {record.status === 'confirmed' && (
            <button onClick={() => onPost(record.id)} disabled={isMutating}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors flex items-center gap-1.5 disabled:opacity-50">
              <ArrowDownRight className="h-3.5 w-3.5" /> {isMutating ? 'Posting…' : 'Post — Update Population'}
            </button>
          )}
          {(record.status === 'draft' || record.status === 'pending_approval') && (
            <button onClick={() => onCancel(record.id)} disabled={isMutating}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors text-muted-foreground disabled:opacity-50">
              Cancel Sale
            </button>
          )}
          <button className="ml-auto px-3 py-2 text-sm font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors flex items-center gap-1.5 text-muted-foreground">
            <Printer className="h-3.5 w-3.5" /> Print
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function BirdSalesContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [farmFilter, setFarmFilter] = useState<string>('all');
  const [showForm, setShowForm] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<BirdSaleRecord | null>(null);
  const [showFilters, setShowFilters] = useState(false);

  // ── Live data via TanStack Query ──────────────────────────────────────────
  const { data: records = [], isLoading } = useSaleRecords();
  const createSale = useCreateSaleRecord();
  const updateSale = useUpdateSaleRecord();

  const filtered = records.filter(r => {
    const matchSearch = !search ||
      r.saleNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.flockNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.customerName.toLowerCase().includes(search.toLowerCase()) ||
      r.farm.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    const matchFarm = farmFilter === 'all' || r.farm === farmFilter;
    return matchSearch && matchStatus && matchFarm;
  });

  function handleSubmit(data: Omit<BirdSaleRecord, 'id' | 'saleNumber' | 'createdAt'>) {
    createSale.mutate(data, {
      onSuccess: (created) => {
        toast.success(`Sale ${created.saleNumber} saved`);
        setShowForm(false);
      },
      onError: () => toast.error('Failed to save sale record'),
    });
  }

  function handleApprove(id: string) {
    updateSale.mutate(
      { id, patch: { status: 'confirmed', confirmedAt: new Date().toISOString(), approvedBy: 'Operations Director' } },
      {
        onSuccess: (updated) => {
          toast.success(`Sale ${updated.saleNumber} confirmed`);
          setSelectedRecord(updated);
        },
        onError: () => toast.error('Failed to confirm sale'),
      },
    );
  }

  function handleReject(id: string, reason: string) {
    updateSale.mutate(
      { id, patch: { status: 'rejected', rejectionReason: reason, rejectedBy: 'Operations Director' } },
      {
        onSuccess: (updated) => {
          toast.success(`Sale ${updated.saleNumber} rejected`);
          setSelectedRecord(updated);
        },
        onError: () => toast.error('Failed to reject sale'),
      },
    );
  }

  function handlePost(id: string) {
    updateSale.mutate(
      { id, patch: { status: 'posted', postedAt: new Date().toISOString() } },
      {
        onSuccess: (updated) => {
          toast.success(`Sale ${updated.saleNumber} posted — flock population updated`);
          setSelectedRecord(updated);
        },
        onError: () => toast.error('Failed to post sale'),
      },
    );
  }

  function handleCancel(id: string) {
    updateSale.mutate(
      { id, patch: { status: 'cancelled' } },
      {
        onSuccess: (updated) => {
          toast.success(`Sale ${updated.saleNumber} cancelled`);
          setSelectedRecord(updated);
        },
        onError: () => toast.error('Failed to cancel sale'),
      },
    );
  }

  const statusCounts = {
    all: records.length,
    draft: records.filter(r => r.status === 'draft').length,
    pending_approval: records.filter(r => r.status === 'pending_approval').length,
    confirmed: records.filter(r => r.status === 'confirmed').length,
    posted: records.filter(r => r.status === 'posted').length,
    rejected: records.filter(r => r.status === 'rejected').length,
  };

  return (
    <div className="flex-1 overflow-y-auto bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl font-semibold text-foreground tracking-tight">Bird Sales</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Record confirmed bird sales as population-OUT movements with revenue traceability.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors text-muted-foreground">
              <Download className="h-4 w-4" /> Export
            </button>
            <button onClick={() => setShowForm(true)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors">
              <Plus className="h-4 w-4" /> Record Sale
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <SalesSummaryCards records={records} />

        {/* Workflow Info Banner */}
        <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-3 flex items-start gap-3">
          <ArrowDownRight className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-800">
            <span className="font-semibold">Sale-to-Population Workflow: </span>
            Draft → Pending Approval → Confirmed → <strong>Posted</strong> (population reduced). Only <em>Posted</em> sales create a Bird Movement OUT and reduce flock inventory.
          </div>
        </div>

        {/* Filters & Search */}
        <div className="bg-white rounded-xl border border-border/60 shadow-sm p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="text" placeholder="Search by sale number, flock, customer, farm…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-border bg-white hover:bg-gray-50 transition-colors">
              <Filter className="h-4 w-4" /> Filters <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3 pt-3 border-t border-border/60">
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">Farm</label>
                <select className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={farmFilter} onChange={e => setFarmFilter(e.target.value)}>
                  <option value="all">All Farms</option>
                  {FARMS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="flex gap-1 mt-3 flex-wrap">
            {(['all', 'draft', 'pending_approval', 'confirmed', 'posted', 'rejected'] as const).map(s => {
              const label = s === 'all' ? 'All' : STATUS_CONFIG[s as BirdSaleStatus]?.label ?? s;
              const count = statusCounts[s];
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${statusFilter === s ? 'bg-primary text-white' : 'bg-gray-100 text-muted-foreground hover:bg-gray-200'}`}>
                  {label} {count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-border/60 shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="py-12 text-center text-sm text-muted-foreground">Loading sale records…</div>
          ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-gray-50">
                  {['Sale #', 'Date', 'Flock', 'Farm / House', 'Qty', 'Revenue (UGX)', 'Customer', 'Payment', 'Status', 'Movement', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40">
                {filtered.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-muted-foreground text-sm">No bird sale records found.</td></tr>
                ) : filtered.map(r => {
                  const sc = STATUS_CONFIG[r.status];
                  const pc = PAYMENT_STATUS_CONFIG[r.paymentStatus];
                  return (
                    <tr key={r.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="px-4 py-3 font-medium text-primary whitespace-nowrap">{r.saleNumber}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{r.saleDate}</td>
                      <td className="px-4 py-3"><p className="font-medium text-foreground">{r.flockNumber}</p><p className="text-xs text-muted-foreground">{r.birdType} · {r.breed}</p></td>
                      <td className="px-4 py-3"><p className="text-foreground">{r.farm}</p><p className="text-xs text-muted-foreground">{r.house}</p></td>
                      <td className="px-4 py-3"><span className="font-semibold text-red-600">−{r.quantity.toLocaleString()}</span><p className="text-xs text-muted-foreground">birds</p></td>
                      <td className="px-4 py-3"><p className="font-semibold text-green-700">{r.totalRevenue.toLocaleString()}</p><p className="text-xs text-muted-foreground">@ {r.unitPrice.toLocaleString()}/bird</p></td>
                      <td className="px-4 py-3"><p className="font-medium text-foreground">{r.customerName}</p><p className="text-xs text-muted-foreground">{DELIVERY_LABELS[r.deliveryMethod]}</p></td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: pc.bg, color: pc.color }}>{pc.label}</span>
                        {r.balance > 0 && <p className="text-xs text-amber-600 mt-0.5">{r.balance.toLocaleString()} due</p>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: sc.bg, color: sc.color }}>
                          <sc.icon className="h-3 w-3" />{sc.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">{r.movementId ? <span className="text-xs font-medium text-primary">{r.movementId}</span> : <span className="text-xs text-muted-foreground">—</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => setSelectedRecord(r)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-muted-foreground" title="View Details">
                            <Eye className="h-3.5 w-3.5" />
                          </button>
                          {r.status === 'pending_approval' && (
                            <button onClick={() => handleApprove(r.id)} disabled={updateSale.isPending}
                              className="p-1.5 rounded-lg hover:bg-green-100 transition-colors text-green-600 disabled:opacity-50" title="Confirm Sale">
                              <CheckCircle className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {r.status === 'confirmed' && (
                            <button onClick={() => handlePost(r.id)} disabled={updateSale.isPending}
                              className="p-1.5 rounded-lg hover:bg-primary/10 transition-colors text-primary disabled:opacity-50" title="Post — Update Population">
                              <ArrowDownRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          )}
          {filtered.length > 0 && (
            <div className="px-4 py-3 border-t border-border/60 bg-gray-50 flex items-center justify-between text-xs text-muted-foreground">
              <span>Showing {filtered.length} of {records.length} records</span>
              <span>Total Revenue (Posted): UGX {records.filter(r => r.status === 'posted').reduce((s, r) => s + r.totalRevenue, 0).toLocaleString()}</span>
            </div>
          )}
        </div>
      </div>

      {showForm && (
        <RecordSaleForm onClose={() => setShowForm(false)} onSubmit={handleSubmit} isPending={createSale.isPending} />
      )}
      {selectedRecord && (
        <DetailDrawer
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onPost={handlePost}
          onCancel={handleCancel}
          isMutating={updateSale.isPending}
        />
      )}
    </div>
  );
}
